import React from 'react';
import { LayoutLogin } from '@/components/layout';
import AppRoutes from '@/const/routes';
import { authService } from '@/api';
import { useNotification } from '@/hooks';
import { InputEmail } from '@/components/form';
import { FieldValues, useForm } from 'react-hook-form';
import { Button, Typography } from '@/components/common';
import { formatErrorMessage } from '@/lib';
import { useRouter } from 'next/router';

export const VerifyEmailScreen: React.FC = () => {
	const router = useRouter();
	const rules = {
		email: {
			required: { value: true, message: 'This is required.' },
		},
	};

	const { toastSuccess } = useNotification();
	const [isLoading, setIsLoading] = React.useState<boolean>(false);
	const [userEmail, setUserEmail] = React.useState<string>();

	const {
		register,
		handleSubmit,
		setValue,
		formState: { errors, isDirty, isValid },
	} = useForm({ mode: 'onSubmit' });

	const handleSubmitData = async (values: FieldValues): Promise<void> => {
		setIsLoading(true);
		try {
			await authService.resendVerificationEmail(values.email);
			setUserEmail(values.email);
			toastSuccess('Verification email has been successfully sent.');
		} catch (error) {
			router.push(AppRoutes.CLIENTAPP_AUTH_SIGN_IN);
			setTimeout(() => {
				toastSuccess(formatErrorMessage((error as any).message));
			}, 500);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<LayoutLogin
			title={userEmail ? 'Check your email' : 'Verify your email'}
			buttonBack
			hrefButtonBack={AppRoutes.CLIENTAPP_AUTH_SIGN_IN}
			buttonLabelBack="Return to Sign In"
		>
			{userEmail && (
				<div className="space-y-8">
					<div>
						<Typography type="regular-18" className="text-center opacity-60">
							{`We have sent a verification email to ${userEmail}`}.
						</Typography>
						<Typography type="regular-18" className="text-center opacity-60">
							{`Don’t forget to check your Spam folder.`}
						</Typography>
					</div>
					<div className="flex justify-center items-center gap-1">
						<Typography type="regular-16" className="opacity-60">
							{`Didn’t receive the email?`}
						</Typography>
						<button onClick={() => handleSubmitData({ email: userEmail })}>
							<Typography
								type="semibold-16"
								className="text-primary hover:opacity-60"
							>
								Resend Verification Email
							</Typography>
						</button>
					</div>
				</div>
			)}
			{!userEmail && (
				<form className="w-full" onSubmit={handleSubmit(handleSubmitData)}>
					<InputEmail
						dataCY="email"
						name="email"
						title="Email"
						register={register}
						rules={rules.email}
						error={errors.email}
						className="bg-white"
						setValueCustom={setValue}
					/>
					<Button
						dataCY="signin"
						type="submit"
						size="full"
						decoration="primary"
						label="Resend Verification Email"
						loading={isLoading}
						disabled={isLoading || !isDirty || !isValid}
						className="my-8"
					/>
				</form>
			)}
		</LayoutLogin>
	);
};
